import { useEffect, useState } from 'react';
import { authService } from './authService';
import { isMiniApp, initTelegram, getInitDataForAuth } from '../../telegram';

/**
 * In the Telegram mini-app, silently authenticate on launch using the signed
 * initData — there is no login form. Returns whether the initial auth attempt
 * has settled, so the app can hold rendering behind a splash until the session
 * is (or isn't) established. Outside the mini-app it's a no-op that returns true.
 */
export const useTelegramAutoAuth = (): boolean => {
  const [ready, setReady] = useState<boolean>(() => !isMiniApp());

  useEffect(() => {
    if (!isMiniApp()) return;

    initTelegram();

    // A local flag can outlive the session cookie. Every Telegram launch must
    // establish a fresh session from the signed launch data.
    const initData = getInitDataForAuth();
    if (!initData) {
      // Opened as mini-app but no initData (e.g. plain browser hit): proceed
      // unauthenticated; private pages still guard themselves.
      setReady(true);
      return;
    }

    let cancelled = false;
    authService
      .loginWithTelegram(initData)
      .catch((err) => console.error('Telegram auto-auth failed', err))
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return ready;
};
